import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { tvpil } from '../compat/tvpil';
import { useInfiniteQueryWithLimit } from '../hooks/useInfiniteQueryWithLimit';
import { useRefetchQuery } from '../hooks/useRefetchQuery';
import { queryKeys } from '../queryKeys';

import { fetchRecommendations } from './fetchRecommendations';

type useRecommendationsArgs = {
  referenceId: string;
  pageSize: number;
  useLimit?: boolean;
};

export function useRecommendations({ referenceId, pageSize, useLimit }: useRecommendationsArgs) {
  const category = useCategoryTreeItem(referenceId);
  const limit = useLimit ? category?.railProperty.railItemsLimit : undefined;
  const enabled = Boolean(category);

  const {
    data,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch: refetchRecommendations,
  } = useInfiniteQueryWithLimit({
    queryKey: queryKeys.recommendations(referenceId, pageSize),
    queryFn: ({ pageParam }) =>
      fetchRecommendations({
        pageIndex: pageParam,
        pageSize,
        partnerListTypeIn: String(category!.partnerListTypeIn || tvpil.dms.savedInterestItemsList),
        groupBy: category!.groupBy,
        kSql: category!.aggregatedKSQL,
      }),
    pageSize,
    enabled,
    limit,
  });

  const { items, totalCount } = useMemo(() => {
    if (!data) {
      return { items: [], totalCount: 0 };
    }

    let items = data.pages.map((it) => it.objects).flat();

    const totalCount = data.pages[0].totalCount;
    const expectedItemsCount = Math.min(data.pages.length * pageSize, totalCount);
    const adjustedTotalCount = totalCount - (expectedItemsCount - items.length);

    if (limit) {
      items = items.slice(0, limit);
    }

    return {
      items,
      totalCount: adjustedTotalCount,
    };
  }, [data, limit, pageSize]);

  const refetch = useRefetchQuery({ enabled, refetch: refetchRecommendations });

  return {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
}
